import { Box, Stack, MenuItem, Select, Tabs, Button, IconButton, Table, TableBody, TableContainer, TableHead, TableRow, Divider } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { EldLandmark, Organization } from "src/models";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { SmallTab } from "src/components/small-tab";
import { TruxTableCell } from "src/components/trux-table-cell";
import { TruxDeleteTableCell } from "src/components/trux-delete-table-cell";
import TruxSelectField from "src/components/inputs/trux-select-field";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import { useAuthContext } from 'src/auth/hooks';
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";


type EldLandmarkModalProps = {
  selectedEldLandmarkModel: ModelSavingData<EldLandmark>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateEldLandmark: VoidFunction,
  organizations: Organization[],
}

export function EldLandmarkModal(
  { selectedEldLandmarkModel, isOpen, setIsOpen, updateEldLandmark, organizations }: EldLandmarkModalProps
) {
  const [selectedTab, setSelectedTab] = useState("general");
  const context = useAuthContext();
  const isParent = context?.session?.organization?.isParent;

  const selectedOrganizations: string[] = selectedEldLandmarkModel.model?.appliesToOrganizationIds.map(id => organizations.find(v => v.id === id)).map(v => v?.organizationName).filter(v => v) as string[] | [];

  return selectedEldLandmarkModel.model && <SimpleDialog
    title={selectedEldLandmarkModel.isNew ? "New Landmark" : "Update Landmark"}
    open={isOpen}
    size="medium"
    onCancel={() => {
      setIsOpen(false);
    }}
    onSubmit={updateEldLandmark}
    submitDisabled={selectedEldLandmarkModel.loading}
    submitButton={selectedEldLandmarkModel.isNew ? "New Landmark" : "Update Landmark"}
    submitButtonIcon={selectedEldLandmarkModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
    submitButtonColor={selectedEldLandmarkModel.isNew ? "success" : "info"}>

    <Tabs
      value={selectedTab}
      onChange={(_, value) => setSelectedTab(value)}
      sx={{
        mb: 2,
        mt: -2,
        minHeight: "auto"
      }}
    >
      {['general', 'emails', 'assets'].map((tab) => (
        <SmallTab
          key={tab}
          iconPosition="end"
          value={tab}
          label={tab}
          sx={{ textTransform: 'capitalize' }}
        />
      ))}
    </Tabs>

    <DisplayErrors savingData={selectedEldLandmarkModel} />

    {selectedTab === "general" && <Stack direction="column" gap={1}>
      <TruxFormField label="Name">
        <TruxTextField modelSavingData={selectedEldLandmarkModel} field="name" />
      </TruxFormField>

      <TruxFormField label="Type">
        <TruxSimpleSelectField value={selectedEldLandmarkModel.model?.landmarkType || "None"} onChange={(val) => selectedEldLandmarkModel.update(model => { model.landmarkType = val === "None" ? "" : val })}>
          <MenuItem value="None" selected={selectedEldLandmarkModel.model?.landmarkType === ""}>None</MenuItem>
          <MenuItem value="Yard" selected={selectedEldLandmarkModel.model?.landmarkType === "Yard"}>Yard</MenuItem>
          <MenuItem value="Terminal" selected={selectedEldLandmarkModel.model?.landmarkType === "Terminal"}>Terminal</MenuItem>
          <MenuItem value="Pool" selected={selectedEldLandmarkModel.model?.landmarkType === "Pool"}>Pool</MenuItem>
          <MenuItem value="Customer Site" selected={selectedEldLandmarkModel.model?.landmarkType === "Customer Site"}>Customer Site</MenuItem>
        </TruxSimpleSelectField>
      </TruxFormField>

      <TruxFormField label="Address">
        <Stack spacing={1}>
          <Box display="flex" gap={2}>
            <Box flexGrow={1}>
              <TruxSimpleTextField label="Street 1" value={selectedEldLandmarkModel.model.mainAddress.street1} onChange={(val) => selectedEldLandmarkModel.update((eldLandmark) => {
                eldLandmark.mainAddress.street1 = val;
              })} />
            </Box>
            <Box flexGrow={1}>
              <TruxSimpleTextField label="Street 2" value={selectedEldLandmarkModel.model.mainAddress.street2} onChange={(val) => selectedEldLandmarkModel.update((eldLandmark) => {
                eldLandmark.mainAddress.street2 = val;
              })} />
            </Box>
          </Box>
          <Box display="flex" gap={2}>
            <Box flexGrow={1}>
              <TruxSimpleTextField label="City" value={selectedEldLandmarkModel.model.mainAddress.city} onChange={(val) => selectedEldLandmarkModel.update((eldLandmark) => {
                eldLandmark.mainAddress.city = val;
              })} />
            </Box>
            <Box flexGrow={1}>
              <TruxSimpleTextField label="State" value={selectedEldLandmarkModel.model.mainAddress.state} onChange={(val) => selectedEldLandmarkModel.update((eldLandmark) => {
                eldLandmark.mainAddress.state = val;
              })} />
            </Box>
            <Box flexGrow={1}>
              <TruxSimpleTextField label="Zip" value={selectedEldLandmarkModel.model.mainAddress.zip} onChange={(val) => selectedEldLandmarkModel.update((eldLandmark) => {
                eldLandmark.mainAddress.zip = val;
              })} />
            </Box>
          </Box>
        </Stack>
      </TruxFormField>
      <TruxFormField label="Active">
        <TruxCheckboxField modelSavingData={selectedEldLandmarkModel} field="active" />
      </TruxFormField>
      <TruxFormField label="Report Dormancy">
        <TruxCheckboxField modelSavingData={selectedEldLandmarkModel} field="reportDormancy" />
      </TruxFormField>

      {selectedEldLandmarkModel.model?.reportDormancy &&
        <TruxFormField label="Min Duration Days">
          <TruxSelectField
            modelSavingData={selectedEldLandmarkModel}
            field="dormancyDurationDays"
          >
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={7}>7</MenuItem>
            <MenuItem value={14}>14</MenuItem>
            <MenuItem value={21}>21</MenuItem>
            <MenuItem value={28}>28</MenuItem>
          </TruxSelectField>
        </TruxFormField>
      }
    </Stack>}

    {selectedTab === "emails" && <Stack>
      <Box sx={{ marginLeft: -3, marginRight: -3 }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {selectedEldLandmarkModel.model.emails.length > 1 && <TruxTableCell sx={{ maxWidth: "30px" }} />}
                <TruxTableCell>
                  Email
                </TruxTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedEldLandmarkModel.model.emails.map((email, index) => <TableRow key={index}>
                {(selectedEldLandmarkModel?.model?.emails.length || 0) > 1 &&
                  <TruxDeleteTableCell>
                    <IconButton color="error" onClick={() => {
                      selectedEldLandmarkModel.update((model) => {
                        model.emails.splice(index, 1);
                      })
                    }}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  </TruxDeleteTableCell>
                }
                <TruxTableCell>
                  <TruxSimpleTextField value={email} onChange={(val) => {
                    selectedEldLandmarkModel.update((model) => {
                      model.emails[index] = val;
                    })
                  }} />
                </TruxTableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box>
        <Button size="small" variant="soft" onClick={() => {
          selectedEldLandmarkModel.update((model) => {
            model.emails.push("");
          })
        }} startIcon={<Iconify icon="mingcute:add-line" />}>
          Add Email
        </Button>
      </Box>
      <Divider sx={{mt: 2, mb: 2}} />
      <Box sx={{mb: 2}}>
        <Box display="flex" flexDirection="row" alignItems="center" gap="10px">
          <Box sx={{textAlign: "right"}}>Email on Action</Box>
          <Box sx={{maxWidth: "200px"}}>
            <TruxSelectField modelSavingData={selectedEldLandmarkModel} field="notifyType" >
              <MenuItem value={0}>In</MenuItem>
              <MenuItem value={1}>Out</MenuItem>
              <MenuItem value={2}>In and Out</MenuItem>
            </TruxSelectField>
          </Box>
        </Box>
      </Box>
    </Stack>}
    {selectedTab === "assets" && <Stack direction="column" gap={1}>
      <TruxFormField label="Applies to all">
        <TruxCheckboxField modelSavingData={selectedEldLandmarkModel} field="appliesToAll" />
      </TruxFormField>

      {!selectedEldLandmarkModel.model.appliesToAll && <>
        {isParent && <TruxFormField label="Organizations">
          <Select
            sx={{minWidth: "100%"}}
            multiple
            value={selectedOrganizations}
            onChange={(e) => {
              const selected = e.target.value;
              if (selected.includes("All")) {
                selectedEldLandmarkModel.update(model => {
                  model.appliesToOrganizationIds = organizations.map(o => o.id);
                })
                return;
              }
              const organizationsFiltered = organizations.filter(v => selected.includes(v.organizationName));

              selectedEldLandmarkModel.update(model => {
                model.appliesToOrganizationIds = organizationsFiltered.map(v => v.id);
              })
            }}
            MenuProps={MenuProps}
          >
            <MenuItem
              value="All"
            >
              All
            </MenuItem>
            {organizations.map((organization) => (
              <MenuItem
                key={organization.id}
                value={organization.organizationName}
              >
                {organization.organizationName}
              </MenuItem>
            ))}
          </Select>
        </TruxFormField>}

        <TruxFormField label="Device Type">
          <Select
            sx={{minWidth: "100%"}}
            multiple
            value={selectedEldLandmarkModel.model.appliesToDeviceType}
            onChange={(e) => {
              const currentlySelected = selectedEldLandmarkModel.model?.appliesToDeviceType || [];
              const selected = e.target.value as number[];

              if (!currentlySelected.includes(-2) && selected.includes(-2)) {
                selectedEldLandmarkModel.update(model => {
                  model.appliesToDeviceType = [-2];
                })
                return;
              }
              if (selected.filter(l => l !== -2).length > 0) {
                selectedEldLandmarkModel.update(model => {
                  model.appliesToDeviceType = selected.filter(l => l !== -2);
                });
              }
            }}
            MenuProps={MenuProps}
          >
            <MenuItem value={-2}>All</MenuItem>
            <MenuItem value={0}>Trucks</MenuItem>
            <MenuItem value={1}>Counting Trucks Trailers</MenuItem>
            <MenuItem value={2}>Samsara Trailers</MenuItem>
            <MenuItem value={3}>Skybitz Trailers</MenuItem>
            <MenuItem value={4}>Premier Trailers</MenuItem>
          </Select>
        </TruxFormField>
      </>}
    </Stack>}
  </SimpleDialog >
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
